import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = process.env.NEXT_PUBLIC_PAYLOAD_ENCRYPTION_KEY;

/**
 * Plain JSON string used for HMAC body hash (same as Postman `jsonString` before `{ payload }` wrap).
 * Stored on the request body object with this Symbol so `JSON.stringify` does not send it.
 */
export const CROSSBORDER_REQUEST_SIGNATURE_PLAINTEXT = Symbol(
  "crossborderRequestSignaturePlaintext",
);

/** Same reference as `request.post(url, body)` → normalized JSON string (more reliable than Symbol alone). */
const signingPlaintextByRequestBody = new WeakMap();

export function getSigningPlaintextForRequestBody(body) {
  if (!body || typeof body !== "object") return null;
  return signingPlaintextByRequestBody.get(body) ?? null;
}

/** Pairs ciphertext → normalized JSON for HMAC when callers use `{ payload: encryptRequest(...) }`. */
const MAX_SIGNING_PLAINTEXT_ENTRIES = 100;
const signingPlaintextByEncryptedPayload = new Map();

function rememberSigningPlaintextForPayload(encryptedPayload, jsonString) {
  if (!encryptedPayload || jsonString == null) return;
  while (signingPlaintextByEncryptedPayload.size >= MAX_SIGNING_PLAINTEXT_ENTRIES) {
    const oldest = signingPlaintextByEncryptedPayload.keys().next().value;
    signingPlaintextByEncryptedPayload.delete(oldest);
  }
  signingPlaintextByEncryptedPayload.set(encryptedPayload, jsonString);
}

/**
 * Used by the axios interceptor: one-shot lookup of the plaintext that was encrypted into this payload.
 * @param {string} encryptedPayload
 * @returns {string|null}
 */
export function consumeSigningPlaintextForEncryptedPayload(encryptedPayload) {
  if (!encryptedPayload || typeof encryptedPayload !== "string") return null;
  const plain = signingPlaintextByEncryptedPayload.get(encryptedPayload);
  if (plain != null) {
    signingPlaintextByEncryptedPayload.delete(encryptedPayload);
  }
  return plain ?? null;
}

function normalizeCrossborderJsonString(data) {
  return JSON.stringify(JSON.parse(JSON.stringify(data)));
}

function encryptNormalizedJsonString(jsonString) {
  if (!ENCRYPTION_KEY) {
    console.error("NEXT_PUBLIC_PAYLOAD_ENCRYPTION_KEY is not defined");
    return null;
  }

  const derivedKey = CryptoJS.SHA256(ENCRYPTION_KEY).toString(CryptoJS.enc.Hex);
  const key = CryptoJS.enc.Hex.parse(derivedKey);
  const iv = CryptoJS.lib.WordArray.random(16);

  const encrypted = CryptoJS.AES.encrypt(jsonString, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return `${CryptoJS.enc.Base64.stringify(iv)}:${CryptoJS.enc.Base64.stringify(encrypted.ciphertext)}`;
}

/**
 * Decrypt response payload from API (works for both Cross Border and Local Transfer)
 * @param {string} encryptedPayload - The encrypted payload in format "base64_iv:base64_ciphertext"
 * @returns {object|null} - Decrypted JSON object or null if decryption fails
 */
export function decryptResponse(encryptedPayload) {
  try {
    if (!ENCRYPTION_KEY) {
      console.error("NEXT_PUBLIC_PAYLOAD_ENCRYPTION_KEY is not defined");
      return null;
    }

    // 1. Derive Key using SHA-256
    const derivedKey = CryptoJS.SHA256(ENCRYPTION_KEY).toString(
      CryptoJS.enc.Hex,
    );
    const key = CryptoJS.enc.Hex.parse(derivedKey);

    // 2. Split IV and Ciphertext
    const [base64Iv, base64Ciphertext] = encryptedPayload.split(":");

    if (!base64Iv || !base64Ciphertext) {
      console.error(
        'Invalid payload format. Expected "base64_iv:base64_ciphertext"',
      );
      return null;
    }

    // 3. Decode and Decrypt
    const iv = CryptoJS.enc.Base64.parse(base64Iv);
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: CryptoJS.enc.Base64.parse(base64Ciphertext) },
      key,
      { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 },
    );

    // 4. Parse Result
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
}

/**
 * Encrypt request payload (returns ciphertext string only).
 *
 * Registers the normalized inner JSON for this ciphertext so device-scoped HMAC still works when
 * callers use the legacy shape `request.post(url, { payload: encryptRequest(data) })` (see
 * {@link consumeSigningPlaintextForEncryptedPayload}). Prefer {@link encryptCrossborderRequestBody}
 * as the POST body when you can (no map, no ordering assumptions).
 *
 * @param {object} data - The data object to encrypt
 * @returns {string|null} - Encrypted payload in format "base64_iv:base64_ciphertext"
 */
export function encryptRequest(data) {
  try {
    const jsonString = normalizeCrossborderJsonString(data);
    const payload = encryptNormalizedJsonString(jsonString);
    if (payload) {
      rememberSigningPlaintextForPayload(payload, jsonString);
    }
    return payload;
  } catch (error) {
    console.error("Encryption failed:", error);
    return null;
  }
}

/**
 * Same encryption as {@link encryptRequest}, but returns the wire body `{ payload }` plus a
 * non-serialized signing plaintext for HMAC (axios interceptor in helpers.js).
 * Use when you pass this object directly as `request.post(url, body)` (not double-wrapped).
 * @param {object} data
 * @returns {{ payload: string }|null}
 */
export function encryptCrossborderRequestBody(data) {
  try {
    const jsonString = normalizeCrossborderJsonString(data);
    const payload = encryptNormalizedJsonString(jsonString);
    if (!payload) return null;

    const body = { payload };
    signingPlaintextByRequestBody.set(body, jsonString);
    rememberSigningPlaintextForPayload(payload, jsonString);
    Object.defineProperty(body, CROSSBORDER_REQUEST_SIGNATURE_PLAINTEXT, {
      value: jsonString,
      enumerable: false,
      configurable: true,
    });
    return body;
  } catch (error) {
    console.error("Encryption failed:", error);
    return null;
  }
}
